import { timeout } from 'widgets/toolbox/util';
import { errorFallbackMessage } from 'widgets/toolbox/ajax';
import { scrollToTop, scrollWindowTo } from 'core/toolbox/scroll';

import { TBasicInput, TBasicInputInstance } from 'widgets/forms/BasicInput';
import { TDateNumericPicker } from 'core/account/DateNumericPicker';

type TDataEventBus = {
    eventId: string;
    eventData: Record<string, string>;
};

/**
 * @description AjaxForm extending
 * @param Widget AjaxForm widget for extending
 * @returns AjaxFormExt class
 */

function AjaxFormExtClassCreator(AjaxForm: import('widgets/forms/AjaxForm').TAjaxForm) {
    /**
     * @category widgets
     * @subcategory forms
     * @class AjaxFormExt
     * @augments Widget
     * @classdesc AjaxFormExt extends AjaxForm widget, change onSubmitted method to custom focus element if needed
     */
    class AjaxFormExt extends AjaxForm {
        dateNumericPickerProcessed = false;

        redirected = false;

        prefs() {
            return {
                successMessage: '',
                successMessageLabel: 'successMessageLabel',
                refToHideAfterSuccess: '',
                scrollToError: true,
                clearFormFieldsOnSuccess: false,
                gtmEventName: '',
                gtmEventData: {},
                errorMessage: '',
                ...super.prefs()
            };
        }

        /**
         * @description Handles server response
         * @emits AjaxForm#submit
         * @param data Server JSON response once form submitted
         */
        onSubmitted(data: TServerResponse) {
            if (data.success) {
                this.handleSuccess(data);
            } else {
                this.handleFailure(data);
            }
        }

        private handleSuccess(data: TServerResponse) {
            if (this.prefs().gtmEventName?.length) {
                this.handleGTMUpdates(this.prefs().gtmEventName, this.prefs().gtmEventData);
            }

            if (data.redirectUrl) {
                this.redirected = true;
                window.location.assign(data.redirectUrl);

                return;
            }

            if (data.isReload) {
                this.redirected = true;
                window.location.reload();

                return;
            }

            if (data.setFields && typeof data.setFields === 'object') {
                this.setFields(data.setFields);
            }

            timeout(() => {
                /**
                 * @description Event to submit AjaxForm
                 * @event AjaxForm#submit
                 */
                this.emit('submit', data);
                this.setSuccess(data);
            });
        }

        private handleFailure(data: TServerResponse) {
            if (data.redirectUrl) {
                this.redirected = true;
                window.location.assign(data.redirectUrl);
            } else if (data.msg) {
                timeout(() => {
                    /**
                     * @description Event to fail submit AjaxForm
                     * @event AjaxForm#submitfail
                     */
                    this.emit('submitfail', data);
                });
            } else if (data.fieldErrors && Object.keys(data.fieldErrors).length) {
                this.processFieldErrors(data.fieldErrors as { [key: string]: string });
            } else if (data.errorCode === undefined && !data.queryString) {
                this.setError(this.prefs().errorMessage || data.errorMessage, this.prefs().scrollToError);
                this.eachField(field => field.clearError());
            } else if (data.errorCode !== 500) {
                // Show only Accessibility Alert in case of 500 error
                this.setError(data.errorMessage, this.prefs().scrollToError);
                this.eachField(field => field.clearError());
            }
        }

        /**
         * @description Processes field errors and sets them on the respective input fields.
         * @param fieldErrors - An object containing field names as keys and error messages as values.
         */
        private processFieldErrors(fieldErrors: { [key: string]: string }) {
            Object.entries(fieldErrors).forEach(([name, errorMessage], index) => {
                this.getById(name, (input: TBasicInputInstance) => {
                    input.setError(errorMessage);

                    // Set focus to the first invalid input field
                    if (index === 0) {
                        this.focusOnError(input);
                    }
                });
            });
        }

        /**
         * @description Show success message if available
         * @param data Server JSON response once form submitted
         */
        setSuccess(data: TServerResponse) {
            if (!data.success) {
                return;
            }

            const successMessageLabel = this.prefs().successMessageLabel;
            const successMessage = data.successMessage ?? this.prefs().successMessage;
            const showSuccessMessage = data.successMessage ?? data.showSuccessMessage;

            if (showSuccessMessage && successMessage) {
                this.ref(successMessageLabel)
                    .setText(<string>successMessage)
                    .show();
            }

            if (this.prefs().refToHideAfterSuccess) {
                this.ref(this.prefs().refToHideAfterSuccess).hide();
            }

            if (this.prefs().clearFormFieldsOnSuccess) {
                this.clearFormFields();
            }

            if (data && data.eventBus) {
                const eventBusData = data.eventBus as TDataEventBus;

                this.eventBus().emit(eventBusData.eventId, eventBusData.eventData);
            }
        }

        /**
         * @description Set focus and scroll to the first invalid input field
         * @param input - input field to focus
         */
        private focusOnError(input: TBasicInputInstance) {
            /* if error is shown outside of edited field(some inputHidden widget only used to show error),
                link to edited field and focus and scroll to it */
            const focusElementId = String(input.ref('self').data('focusElementId'));

            if (focusElementId && focusElementId !== 'undefined') {
                this.getById(focusElementId, (focusElement: TBasicInputInstance) => {
                    focusElement.setFocus();
                });
            } else {
                scrollWindowTo(input.ref('self').get(), true);
            }
        }

        /**
         * @description Get form field instances
         */
        getFields(): Array<TBasicInputInstance> {
            const BasicInput = <TBasicInput>this.getConstructor('basicInput');
            const items = this.items ? this.items : [];

            const dateNumericPicker = <TDateNumericPicker>this.getConstructor('dateNumericPicker');
            const dateNumericPickerItems = this.items.filter((item: unknown): item is TDateNumericPicker => item instanceof dateNumericPicker);

            if (dateNumericPickerItems.length && !this.dateNumericPickerProcessed) {
                dateNumericPickerItems.forEach(dateNumericPickerItem => {
                    if (dateNumericPickerItem.items.length) {
                        items.push(...dateNumericPickerItem.items);
                    }
                });
                this.dateNumericPickerProcessed = true;
            }

            return items.filter((item: unknown): item is TBasicInputInstance => item instanceof BasicInput);
        }

        /**
         * @description Set form fields values from an object
         * @param setFields - object with field names as keys and values to set
         */
        setFields(setFields) {
            this.getFields().forEach(field => {
                const fieldName = typeof field.getName === 'function' ? field.getName() : (field as unknown as { name: string }).name;
                const fieldData = setFields[fieldName];

                if (fieldData !== undefined && typeof fieldData !== 'boolean') {
                    field.setValue(fieldData, false);
                    field.clearError();
                }
            });
        }

        /**
         * @description Clear all form fields
         */
        clearFormFields() {
            this.eachField(widget => {
                if (!widget.initValue && !widget.ref('field').hasAttr('disabled')) {
                    widget.setValue('', false);
                }

                widget.clearError();
            });
        }

        /**
         * @description Handles GTM tracking updates
         */
        handleGTMUpdates(eventName: string, data = {}) {
            this.eventBus().emit(eventName, data);
        }

        /**
         * @description Handles an error, which happens during request (for ex. 500 response)
         * @param error - error, which happened during request
         */
        onError(error: Error) {
            const scrollToError = (error as { scrollToError?: boolean }).scrollToError ?? true;
            const errorElement = this.ref(this.prefs().errorMessageLabel).get();
            const errorMessage = this.prefs().errorMessage || errorFallbackMessage;

            this.setError(error.message || errorMessage, scrollToError);

            if (errorElement) {
                scrollWindowTo(errorElement, true);
            }
        }

        /**
         * @description Set Form or Form fields errors
         * @param msg - Generic error message, if no custom message - show generic one
         * @param scrollToError - defines using of scroll to error
         * @param refElem - RefElement id
         */
        setError(msg: string, scrollToError = true, refElem = 'errorMessageLabel') {
            const errorMessageLabel = this.ref(refElem)
                .setText(msg || '')
                .show();

            if (!scrollToError) {
                return;
            }

            const element = errorMessageLabel.get();

            if (element) {
                scrollWindowTo(element, true);
            } else {
                scrollToTop();
            }
        }

        /**
         * @description Changes Form state after submission
         */
        afterSubmission() {
            if (this.redirected) {
                return;
            }

            super.afterSubmission();
        }
    }

    return AjaxFormExt;
}

export type TAjaxFormExt = ReturnType<typeof AjaxFormExtClassCreator>;

export type TAjaxFormExtInstance = InstanceType<TAjaxFormExt>;

export default AjaxFormExtClassCreator;
